import type { UserRole } from '@/store/authStore';

/**
 * Which role wins when someone holds several, for the one question that can only
 * have one answer: what the app looks like.
 *
 * The client-side twin of `ROLE_PRECEDENCE` in `apps/api/src/services/personRoles.ts`,
 * and deliberately the same order. Two copies rather than a shared package because
 * the API's list includes `super_admin`, which is a platform office the app never
 * renders a view for; a single shared union would drag it into the frontend's
 * types for no benefit. `roles.test.ts` pins the two lists against each other.
 *
 * Never use this to decide *permission* - that is `roles.includes(...)`, which
 * reads the whole list. A teacher who is also a parent must keep every parent
 * page they had; this only settles which dashboard they land on.
 */
export const ROLE_PRECEDENCE: readonly UserRole[] = [
  'admin',
  'coordinator',
  'teacher',
  'parent',
  'student',
] as const;

/**
 * The role that shapes the UI for someone holding `roles`.
 *
 * Undefined for an empty list rather than a default: a person with no live grants
 * is not a student, they are someone whose access has been revoked, and the
 * caller has to decide what that means (`ProtectedRoute` sends them to their
 * dashboard, which is the one page that needs no office).
 */
export function primaryRole(roles: readonly UserRole[] | undefined): UserRole | undefined {
  if (!roles?.length) return undefined;
  return [...roles].sort(
    (a, b) => ROLE_PRECEDENCE.indexOf(a) - ROLE_PRECEDENCE.indexOf(b)
  )[0];
}

/** Does this person hold any of these offices? */
export function holdsAnyRole(
  roles: readonly UserRole[] | undefined,
  wanted: readonly UserRole[]
): boolean {
  return roles?.some((role) => wanted.includes(role)) ?? false;
}
